import { WorkflowBlock } from "@/pages/workflows/edit/flow/WorkflowBlock.tsx";
import {
  useWorkflowMoveBlock,
  workflowBlockMoveMutationKey,
} from "@/queries/workflows/workflow.query.ts";
import { WorkflowBlockNode } from "@/types/workflows/workflow-tree-builder.ts";
import { ApiWorkflowBlockTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import {
  DragDropContext,
  DragStart,
  DragUpdate,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { Stack } from "@skailer-com/uikit";
import { useIsMutating } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const INDENT_SIZE = 24;

interface FlatWorkflowBlock {
  node: WorkflowBlockNode;
  depth: number;
  parentBlockId: number;
  siblingIndex: number;
  subtreeEndIndex: number;
  isLast: boolean;
  continuationLineLevels: number[];
  originalIndex: number;
}

interface DropProjection {
  parentBlockId: number;
  position: number;
  depth: number;
  insertionIndex: number;
}

interface ProjectedDecoration {
  depth: number;
  isLast: boolean;
  continuationLineLevels: number[];
}

const getActionChildren = (node: WorkflowBlockNode) =>
  node.children.filter((child) => child.block.block_type === ApiWorkflowBlockTypes.action);

const flattenWorkflowTree = (root: WorkflowBlockNode): FlatWorkflowBlock[] => {
  const items: FlatWorkflowBlock[] = [];

  const visit = (
    node: WorkflowBlockNode,
    parentBlockId: number,
    siblingIndex: number,
    isLast: boolean,
    continuationLineLevels: number[]
  ) => {
    const originalIndex = items.length;
    const depth = Math.max(0, node.level - 1);
    const item: FlatWorkflowBlock = {
      node,
      depth,
      parentBlockId,
      siblingIndex,
      subtreeEndIndex: originalIndex + 1,
      isLast,
      continuationLineLevels,
      originalIndex,
    };
    items.push(item);

    const children = getActionChildren(node);
    const childContinuationLineLevels =
      isLast || depth === 0 ? continuationLineLevels : [...continuationLineLevels, depth];

    children.forEach((child, index) => {
      visit(
        child,
        node.block.id,
        index,
        index === children.length - 1,
        childContinuationLineLevels
      );
    });
    item.subtreeEndIndex = items.length;
  };

  const children = getActionChildren(root);
  children.forEach((child, index) => {
    visit(child, root.block.id, index, index === children.length - 1, []);
  });

  return items;
};

const getDropProjection = (
  items: FlatWorkflowBlock[],
  sourceIndex: number,
  destinationIndex: number,
  horizontalOffset: number,
  rootBlockId: number
): DropProjection | null => {
  const activeItem = items[sourceIndex];
  if (!activeItem) return null;

  const subtreeStart = sourceIndex;
  const subtreeEnd = activeItem.subtreeEndIndex;
  const originalBoundary = destinationIndex > sourceIndex ? destinationIndex + 1 : destinationIndex;

  if (originalBoundary > subtreeStart && originalBoundary < subtreeEnd) return null;

  const remainingItems = items.filter(
    (item) => item.originalIndex < subtreeStart || item.originalIndex >= subtreeEnd
  );
  const insertionIndex = remainingItems.findIndex((item) => item.originalIndex >= originalBoundary);
  const safeInsertionIndex = insertionIndex === -1 ? remainingItems.length : insertionIndex;
  const previousItem = remainingItems[safeInsertionIndex - 1];
  const nextItem = remainingItems[safeInsertionIndex];
  const maxDepth = previousItem ? previousItem.depth + 1 : 0;
  const minDepth = nextItem ? Math.min(nextItem.depth, maxDepth) : 0;
  const requestedDepth = activeItem.depth + Math.round(horizontalOffset / INDENT_SIZE);
  const depth = Math.max(minDepth, Math.min(requestedDepth, maxDepth));

  let parentBlockId = rootBlockId;
  if (depth > 0) {
    for (let index = safeInsertionIndex - 1; index >= 0; index -= 1) {
      if (remainingItems[index].depth === depth - 1) {
        parentBlockId = remainingItems[index].node.block.id;
        break;
      }
    }
  }

  const position = remainingItems
    .slice(0, safeInsertionIndex)
    .filter((item) => item.parentBlockId === parentBlockId).length;

  return { parentBlockId, position, depth, insertionIndex: safeInsertionIndex };
};

const getProjectedDecorations = (
  items: FlatWorkflowBlock[],
  sourceIndex: number,
  projection: DropProjection,
  rootBlockId: number
) => {
  const activeItem = items[sourceIndex];
  const decorations = new Map<number, ProjectedDecoration>();
  if (!activeItem) return decorations;

  const childrenByParentId = new Map<number, FlatWorkflowBlock[]>();
  items.forEach((item) => {
    if (item.originalIndex === sourceIndex) return;
    const siblings = childrenByParentId.get(item.parentBlockId) ?? [];
    siblings.push(item);
    childrenByParentId.set(item.parentBlockId, siblings);
  });

  const targetSiblings = childrenByParentId.get(projection.parentBlockId) ?? [];
  targetSiblings.splice(Math.min(projection.position, targetSiblings.length), 0, activeItem);
  childrenByParentId.set(projection.parentBlockId, targetSiblings);

  const visit = (parentBlockId: number, parentDepth: number, continuationLineLevels: number[]) => {
    const children = childrenByParentId.get(parentBlockId) ?? [];
    children.forEach((item, index) => {
      const depth = parentBlockId === rootBlockId ? 0 : parentDepth + 1;
      const isLast = index === children.length - 1;
      decorations.set(item.originalIndex, { depth, isLast, continuationLineLevels });

      const childContinuationLineLevels =
        isLast || depth === 0 ? continuationLineLevels : [...continuationLineLevels, depth];
      visit(item.node.block.id, depth, childContinuationLineLevels);
    });
  };

  visit(rootBlockId, -1, []);
  return decorations;
};

const ReadOnlyWorkflowTree = ({
  node,
  workflowVersion,
  isLast,
  continuationLineLevels,
}: {
  node: WorkflowBlockNode;
  workflowVersion: ApiWorkflowVersion;
  isLast: boolean;
  continuationLineLevels: number[];
}) => {
  const children = getActionChildren(node);
  const visualLevel = Math.max(0, node.level - 1);
  const childContinuationLineLevels =
    isLast || visualLevel === 0 ? continuationLineLevels : [...continuationLineLevels, visualLevel];

  return (
    <Stack gap="sm">
      <WorkflowBlock
        node={node}
        isLast={isLast}
        workflowVersion={workflowVersion}
        readOnly
        continuationLineLevels={continuationLineLevels}
      />
      {children.map((child, index) => (
        <ReadOnlyWorkflowTree
          key={`${child.block.id}-${index}`}
          node={child}
          workflowVersion={workflowVersion}
          isLast={index === children.length - 1}
          continuationLineLevels={childContinuationLineLevels}
        />
      ))}
    </Stack>
  );
};

export const WorkflowBlockTreeRenderer = ({
  node,
  workflowVersion,
  isLast = true,
  readOnly = false,
  continuationLineLevels = [],
}: {
  node: WorkflowBlockNode;
  workflowVersion: ApiWorkflowVersion;
  isLast?: boolean;
  readOnly?: boolean;
  continuationLineLevels?: number[];
}) => {
  const items = useMemo(() => flattenWorkflowTree(node), [node]);
  const moveBlock = useWorkflowMoveBlock();
  const isMovePending = useIsMutating({ mutationKey: workflowBlockMoveMutationKey }) > 0;
  const [sourceIndex, setSourceIndex] = useState<number | null>(null);
  const [destinationIndex, setDestinationIndex] = useState<number | null>(null);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  useEffect(() => {
    if (sourceIndex === null || dragStartX === null) return;

    const handleMouseMove = (event: MouseEvent) => setHorizontalOffset(event.clientX - dragStartX);
    const handleTouchMove = (event: TouchEvent) => {
      const clientX = event.touches[0]?.clientX;
      if (clientX !== undefined) setHorizontalOffset(clientX - dragStartX);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [dragStartX, sourceIndex]);

  if (readOnly) {
    return (
      <ReadOnlyWorkflowTree
        node={node}
        workflowVersion={workflowVersion}
        isLast={isLast}
        continuationLineLevels={continuationLineLevels}
      />
    );
  }

  const projection =
    sourceIndex !== null && destinationIndex !== null
      ? getDropProjection(items, sourceIndex, destinationIndex, horizontalOffset, node.block.id)
      : null;
  const remainingItems =
    sourceIndex === null
      ? items
      : items.filter(
          (item) =>
            item.originalIndex < sourceIndex ||
            item.originalIndex >= (items[sourceIndex]?.subtreeEndIndex ?? sourceIndex + 1)
        );
  const indicatorItem = projection ? remainingItems[projection.insertionIndex] : undefined;
  const indicatorBeforeIndex = indicatorItem?.originalIndex;
  const showIndicatorAtEnd = projection && projection.insertionIndex === remainingItems.length;
  const projectedDecorations =
    sourceIndex !== null && projection
      ? getProjectedDecorations(items, sourceIndex, projection, node.block.id)
      : null;

  const resetDrag = () => {
    setSourceIndex(null);
    setDestinationIndex(null);
    setDragStartX(null);
    setHorizontalOffset(0);
  };

  const handleDragStart = (start: DragStart) => {
    setSourceIndex(start.source.index);
    setDestinationIndex(start.source.index);
    setHorizontalOffset(0);
  };

  const handleDragUpdate = (update: DragUpdate) => {
    setDestinationIndex(update.destination?.index ?? null);
  };

  const handleDragEnd = (result: DropResult) => {
    const currentSourceIndex = sourceIndex ?? result.source.index;
    const currentProjection = result.destination
      ? getDropProjection(
          items,
          currentSourceIndex,
          result.destination.index,
          horizontalOffset,
          node.block.id
        )
      : null;
    const activeItem = items[currentSourceIndex];
    if (!activeItem || !currentProjection || activeItem.node.hasMultipleParents) {
      resetDrag();
      return;
    }
    if (
      currentProjection.parentBlockId === activeItem.parentBlockId &&
      currentProjection.position === activeItem.siblingIndex
    ) {
      resetDrag();
      return;
    }

    moveBlock.mutate({
      workflowId: workflowVersion.workflow_id,
      workflowVersionId: workflowVersion.id,
      workflowBlockId: activeItem.node.block.id,
      payload: {
        parent_block_id: currentProjection.parentBlockId,
        position: currentProjection.position,
      },
    });
    resetDrag();
  };

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}
    >
      <Droppable
        droppableId={`workflow-blocks-${workflowVersion.id}`}
        renderClone={(provided, snapshot, rubric) => {
          const activeItem = items[rubric.source.index];
          if (!activeItem) return <div ref={provided.innerRef} />;

          const draggedItems = items.slice(rubric.source.index, activeItem.subtreeEndIndex);

          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{
                ...provided.draggableProps.style,
                transitionDuration: snapshot.isDropAnimating ? "0.001s" : undefined,
              }}
              className="flex flex-col gap-2"
            >
              {draggedItems.map((item, index) => {
                const decoration = projectedDecorations?.get(item.originalIndex);

                return (
                  <WorkflowBlock
                    key={`${item.node.block.id}-${item.originalIndex}`}
                    node={item.node}
                    isLast={decoration?.isLast ?? item.isLast}
                    workflowVersion={workflowVersion}
                    continuationLineLevels={
                      decoration?.continuationLineLevels ?? item.continuationLineLevels
                    }
                    dragHandleProps={index === 0 ? provided.dragHandleProps : undefined}
                    showDragHandle
                    isDragging={snapshot.isDragging}
                    decorativeLevel={decoration?.depth ?? item.depth}
                  />
                );
              })}
            </div>
          );
        }}
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-2">
            {items.map((item, index) => {
              const isDragDisabled = isMovePending || item.node.hasMultipleParents;
              const decoration = projectedDecorations?.get(item.originalIndex);
              const isDescendantOfActive =
                sourceIndex !== null &&
                index > sourceIndex &&
                index < (items[sourceIndex]?.subtreeEndIndex ?? sourceIndex + 1);

              return (
                <Draggable
                  key={`${item.node.block.id}-${index}`}
                  draggableId={`workflow-block-${item.node.block.id}-${index}`}
                  index={index}
                  isDragDisabled={isDragDisabled}
                >
                  {(draggableProvided, snapshot) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={draggableProvided.draggableProps.style}
                      className={`relative ${isDescendantOfActive ? "invisible" : ""}`}
                    >
                      {indicatorBeforeIndex === item.originalIndex && projection && (
                        <div
                          className="absolute top-0 z-10 h-0.5 bg-accent"
                          style={{
                            left: `${projection.depth * INDENT_SIZE}px`,
                            right: 0,
                          }}
                        />
                      )}
                      <WorkflowBlock
                        node={item.node}
                        isLast={decoration?.isLast ?? item.isLast}
                        workflowVersion={workflowVersion}
                        continuationLineLevels={
                          decoration?.continuationLineLevels ?? item.continuationLineLevels
                        }
                        dragHandleProps={draggableProvided.dragHandleProps}
                        showDragHandle
                        isDragDisabled={isDragDisabled}
                        isDragging={snapshot.isDragging}
                        decorativeLevel={decoration?.depth}
                        onDragHandleStart={setDragStartX}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {showIndicatorAtEnd && projection && (
              <div className="relative h-0">
                <div
                  className="absolute bottom-0 z-10 h-0.5 bg-accent"
                  style={{
                    left: `${projection.depth * INDENT_SIZE}px`,
                    right: 0,
                  }}
                />
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
